import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { apiService } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';

const PAYMENT_METHODS = [
  { id: 'MOBILE_MONEY', label: 'Mobile Money', icon: 'Smartphone', description: 'MTN, Vodafone, AirtelTigo' },
  { id: 'CARD', label: 'Card Payments', icon: 'CreditCard', description: 'Visa, Mastercard, Amex' },
  { id: 'CRYPTO', label: 'Cryptocurrency', icon: 'Bitcoin', description: 'BTC, ETH, USDT' },
];

const DURATION_OPTIONS = [
  { value: 'ONE_HOUR', label: '1 Hour' },
  { value: 'ONE_DAY', label: '24 Hours' },
  { value: 'SEVEN_DAYS', label: '7 Days' },
  { value: 'THIRTY_DAYS', label: '30 Days' },
  { value: 'CUSTOM', label: 'Custom Date & Time' },
];

// Map API expiration values back to UI enum values for pre-filling
const API_TO_UI_EXPIRATION = {
  ONE_HOUR: 'ONE_HOUR',
  ONE_DAY: 'ONE_DAY',
  SEVEN_DAYS: 'SEVEN_DAYS',
  THIRTY_DAYS: 'THIRTY_DAYS',
  CUSTOM: 'CUSTOM',
};

const EditPaymentLinkModal = ({ link, onClose, onSaved }) => {
  const { businessCode } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [apiError, setApiError] = useState('');

  // Determine initial expiration type from link data
  const initialExpirationType = API_TO_UI_EXPIRATION?.[link?.expirationType] || link?.expirationType || 'ONE_DAY';

  // Build initial selectedMethods from API payment_methods array or legacy object
  const buildInitialMethods = () => {
    if (Array.isArray(link?.payment_methods)) {
      return {
        MOBILE_MONEY: link?.payment_methods?.includes('MOBILE_MONEY'),
        CARD: link?.payment_methods?.includes('CARD'),
        CRYPTO: link?.payment_methods?.includes('CRYPTO'),
      };
    }
    // Legacy object format
    if (link?.selectedMethods) {
      const legacy = link?.selectedMethods;
      return {
        MOBILE_MONEY: !!(legacy?.MOBILE_MONEY || legacy?.mobile_money),
        CARD: !!(legacy?.CARD || legacy?.card),
        CRYPTO: !!(legacy?.CRYPTO || legacy?.crypto),
      };
    }
    return { MOBILE_MONEY: true, CARD: true, CRYPTO: true };
  };

  const [form, setForm] = useState({
    title: link?.title || '',
    amount: link?.amount || '',
    currency: link?.currency || 'KES',
    description: link?.description || '',
    allMethods: link?.allMethods ?? true,
    selectedMethods: buildInitialMethods(),
    expirationType: initialExpirationType,
    customExpiry: initialExpirationType === 'CUSTOM' && link?.expires_at
      ? new Date(link?.expires_at)?.toISOString()?.slice(0, 16)
      : '',
    // Support top-level KYC fields (new API) with fallback to nested address object (legacy)
    billingFirstName: link?.firstName || link?.address?.first_name || link?.billingFirstName || '',
    billingLastName: link?.lastName || link?.address?.last_name || link?.billingLastName || '',
    billingEmail: link?.email || link?.address?.email || link?.billingEmail || '',
    billingPhone: link?.phone || link?.address?.phone || link?.billingPhone || '',
    billingAddress: (typeof link?.address === 'string' ? link?.address : link?.address?.address) || link?.billingAddress || '',
    billingCity: link?.city || link?.address?.city || link?.billingCity || '',
    billingCountry: link?.country || link?.address?.country || link?.billingCountry || '',
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const toggleMethod = (id) => {
    setForm(prev => {
      const updated = { ...prev?.selectedMethods, [id]: !prev?.selectedMethods?.[id] };
      return { ...prev, selectedMethods: updated };
    });
  };

  const validate = () => {
    const e = {};
    if (!form?.title?.trim()) e.title = 'Title is required';
    if (!form?.amount || isNaN(parseFloat(form?.amount)) || parseFloat(form?.amount) <= 0)
      e.amount = 'Enter a valid amount';
    if (!form?.allMethods && !Object.values(form?.selectedMethods)?.some(Boolean))
      e.methods = 'Select at least one payment method';
    if (form?.expirationType === 'CUSTOM' && !form?.customExpiry)
      e.customExpiry = 'Select expiry date and time';
    return e;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const errs = validate();
    if (Object.keys(errs)?.length > 0) { setErrors(errs); return; }
    setLoading(true);
    setApiError('');

    const paymentMethods = form?.allMethods
      ? ['MOBILE_MONEY', 'CARD', 'CRYPTO']
      : Object.keys(form?.selectedMethods)?.filter(k => form?.selectedMethods?.[k]);

    const requestPayload = {
      business_code: businessCode,
      link_code: link?.link_code || link?.link || '',
      title: form?.title,
      currency: form?.currency,
      amount: parseFloat(form?.amount),
      payment_methods: paymentMethods,
      expiration: form?.expirationType,
    };

    if (form?.description?.trim()) {
      requestPayload.description = form?.description;
    }

    if (form?.expirationType === 'CUSTOM' && form?.customExpiry) {
      requestPayload.custom_expiration = new Date(form?.customExpiry)?.toISOString();
    }

    const hasBillingData = form?.billingFirstName || form?.billingLastName || form?.billingEmail ||
      form?.billingPhone || form?.billingAddress || form?.billingCity || form?.billingCountry;

    if (hasBillingData) {
      requestPayload.address = {
        first_name: form?.billingFirstName || undefined,
        last_name: form?.billingLastName || undefined,
        email: form?.billingEmail || undefined,
        phone: form?.billingPhone || undefined,
        address: form?.billingAddress || undefined,
        city: form?.billingCity || undefined,
        country: form?.billingCountry || undefined,
      };
    }

    try {
      const response = await apiService?.updatePaymentLink(requestPayload);
      const data = response?.data;
      const detail = data?.details?.[0] || {};

      if (onSaved) {
        onSaved({
          ...link,
          ...detail,
          title: detail?.title || form?.title,
          amount: detail?.amount || form?.amount,
          currency: detail?.currency || form?.currency,
          description: form?.description,
          allMethods: form?.allMethods,
          selectedMethods: form?.selectedMethods,
          expirationType: form?.expirationType,
          customExpiry: form?.customExpiry,
          expiryDate: detail?.expires_at || link?.expiryDate,
          payment_methods: detail?.payment_methods || paymentMethods,
          link_code: detail?.link_code || link?.link_code,
        });
      }
      setLoading(false);
      setSaved(true);
      setTimeout(() => onClose(), 1200);
    } catch (err) {
      const errMsg = err?.response?.data?.status_description
        || err?.response?.data?.message
        || err?.response?.data?.detail
        || 'Failed to update payment link. Please try again.';
      setApiError(errMsg);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-surface rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col border border-border">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="Pencil" size={18} className="text-warning" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-text-primary">Edit Payment Link</h2>
              <p className="text-xs text-text-secondary font-mono">{link?.id}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted text-text-secondary transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5">
          {saved ? (
            <div className="flex flex-col items-center text-center py-10 space-y-4">
              <div className="w-14 h-14 bg-success/10 rounded-full flex items-center justify-center">
                <Icon name="CheckCircle" size={30} className="text-success" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-text-primary">Changes Saved!</h3>
                <p className="text-sm text-text-secondary mt-1">Your payment link has been updated successfully.</p>
              </div>
            </div>
          ) : (
            <form id="edit-link-form" onSubmit={handleSubmit} className="space-y-6">
              {/* API Error */}
              {apiError && (
                <div className="flex items-start space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg">
                  <Icon name="AlertCircle" size={15} className="text-error flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-error">{apiError}</p>
                </div>
              )}

              {/* Payment Details */}
              <div>
                <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center space-x-2">
                  <span className="w-5 h-5 bg-primary text-white rounded-full text-xs flex items-center justify-center font-bold">1</span>
                  <span>Payment Details</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-text-secondary mb-1">Link Title <span className="text-error">*</span></label>
                    <input
                      type="text"
                      value={form?.title}
                      onChange={e => handleChange('title', e?.target?.value)}
                      placeholder="e.g. Invoice #1234 Payment"
                      className={`w-full px-3 py-2 text-sm border rounded-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 ${errors?.title ? 'border-error' : 'border-border'}`}
                    />
                    {errors?.title && <p className="text-xs text-error mt-1">{errors?.title}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Amount <span className="text-error">*</span></label>
                    <div className="flex">
                      <select
                        value={form?.currency}
                        onChange={e => handleChange('currency', e?.target?.value)}
                        className="px-3 py-2 text-sm border border-r-0 border-border rounded-l-lg bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                      >
                        <option>KES</option>
                        <option>USD</option>
                        <option>EUR</option>
                        <option>GBP</option>
                      </select>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={form?.amount}
                        onChange={e => handleChange('amount', e?.target?.value)}
                        placeholder="0.00"
                        className={`flex-1 px-3 py-2 text-sm border rounded-r-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 ${errors?.amount ? 'border-error' : 'border-border'}`}
                      />
                    </div>
                    {errors?.amount && <p className="text-xs text-error mt-1">{errors?.amount}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Description (optional)</label>
                    <input
                      type="text"
                      value={form?.description}
                      onChange={e => handleChange('description', e?.target?.value)}
                      placeholder="Brief description for customer"
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Methods */}
              <div>
                <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center space-x-2">
                  <span className="w-5 h-5 bg-primary text-white rounded-full text-xs flex items-center justify-center font-bold">2</span>
                  <span>Payment Methods <span className="text-error">*</span></span>
                </h3>
                <div className="flex items-center space-x-3 mb-3">
                  <button
                    type="button"
                    onClick={() => handleChange('allMethods', true)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${form?.allMethods ? 'bg-primary text-white border-primary' : 'bg-background text-text-secondary border-border hover:border-primary/50'}`}
                  >
                    All Methods
                  </button>
                  <button
                    type="button"
                    onClick={() => handleChange('allMethods', false)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${!form?.allMethods ? 'bg-primary text-white border-primary' : 'bg-background text-text-secondary border-border hover:border-primary/50'}`}
                  >
                    Select Methods
                  </button>
                </div>
                {errors?.methods && <p className="text-xs text-error mb-2">{errors?.methods}</p>}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {PAYMENT_METHODS?.map(m => {
                    const isEnabled = form?.allMethods || form?.selectedMethods?.[m?.id];
                    return (
                      <button
                        key={m?.id}
                        type="button"
                        onClick={() => !form?.allMethods && toggleMethod(m?.id)}
                        disabled={form?.allMethods}
                        className={`flex items-center space-x-3 p-3 rounded-xl border text-left transition-all ${
                          isEnabled
                            ? 'border-primary bg-primary/5 text-text-primary' : 'border-border bg-background text-text-secondary opacity-60'
                        } ${form?.allMethods ? 'cursor-default' : 'cursor-pointer hover:border-primary/60'}`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isEnabled ? 'bg-primary/10' : 'bg-muted'}`}>
                          <Icon name={m?.icon} size={16} className={isEnabled ? 'text-primary' : 'text-text-secondary'} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold truncate">{m?.label}</p>
                          <p className="text-[10px] text-text-secondary truncate">{m?.description}</p>
                        </div>
                        {!form?.allMethods && (
                          <div className={`ml-auto w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${form?.selectedMethods?.[m?.id] ? 'bg-primary border-primary' : 'border-border'}`}>
                            {form?.selectedMethods?.[m?.id] && <Icon name="Check" size={10} color="white" />}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Expiration */}
              <div>
                <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center space-x-2">
                  <span className="w-5 h-5 bg-primary text-white rounded-full text-xs flex items-center justify-center font-bold">3</span>
                  <span>Expiration <span className="text-error">*</span></span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
                  {DURATION_OPTIONS?.map(opt => (
                    <button
                      key={opt?.value}
                      type="button"
                      onClick={() => handleChange('expirationType', opt?.value)}
                      className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${form?.expirationType === opt?.value ? 'bg-primary text-white border-primary' : 'bg-background text-text-secondary border-border hover:border-primary/50'}`}
                    >
                      {opt?.label}
                    </button>
                  ))}
                </div>
                {form?.expirationType === 'CUSTOM' && (
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Expiry Date & Time <span className="text-error">*</span></label>
                    <input
                      type="datetime-local"
                      value={form?.customExpiry}
                      onChange={e => handleChange('customExpiry', e?.target?.value)}
                      min={new Date()?.toISOString()?.slice(0, 16)}
                      className={`w-full sm:w-64 px-3 py-2 text-sm border rounded-lg bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 ${errors?.customExpiry ? 'border-error' : 'border-border'}`}
                    />
                    {errors?.customExpiry && <p className="text-xs text-error mt-1">{errors?.customExpiry}</p>}
                  </div>
                )}
              </div>

              {/* Billing Address */}
              <div>
                <h3 className="text-sm font-semibold text-text-primary mb-1 flex items-center space-x-2">
                  <span className="w-5 h-5 bg-primary text-white rounded-full text-xs flex items-center justify-center font-bold">4</span>
                  <span>Billing Address <span className="text-text-secondary font-normal text-xs">(optional)</span></span>
                </h3>
                <p className="text-xs text-text-secondary mb-3 ml-7">All billing address fields are optional.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">First Name</label>
                    <input type="text" value={form?.billingFirstName} onChange={e => handleChange('billingFirstName', e?.target?.value)} placeholder="First name" className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Last Name</label>
                    <input type="text" value={form?.billingLastName} onChange={e => handleChange('billingLastName', e?.target?.value)} placeholder="Last name" className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Email</label>
                    <input type="email" value={form?.billingEmail} onChange={e => handleChange('billingEmail', e?.target?.value)} placeholder="customer@email.com" className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Phone</label>
                    <input type="tel" value={form?.billingPhone} onChange={e => handleChange('billingPhone', e?.target?.value)} placeholder="254700111222" className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">City</label>
                    <input type="text" value={form?.billingCity} onChange={e => handleChange('billingCity', e?.target?.value)} placeholder="City" className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-text-secondary mb-1">Street Address</label>
                    <input type="text" value={form?.billingAddress} onChange={e => handleChange('billingAddress', e?.target?.value)} placeholder="Street address" className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Country</label>
                    <input type="text" value={form?.billingCountry} onChange={e => handleChange('billingCountry', e?.target?.value)} placeholder="Country" className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        {!saved && (
          <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-border flex-shrink-0">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-text-secondary border border-border rounded-lg hover:bg-muted transition-colors">
              Cancel
            </button>
            <button
              type="submit"
              form="edit-link-form"
              disabled={loading}
              className="flex items-center space-x-2 px-5 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60"
            >
              {loading ? <Icon name="Loader" size={15} className="animate-spin" /> : <Icon name="Save" size={15} />}
              <span>{loading ? 'Saving...' : 'Save Changes'}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditPaymentLinkModal;
